import React, { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'

import Container from 'components/Container';
import PrivatePage from 'components/PrivatePage';
import Loader from 'components/Loader'
import TituloDaAba from 'components/shared/TituloDaAba';
import ErrorBoundary from 'components/ErrorBoundary';
import useLogin from 'contexts/LoginContext';
import { getUserCachedData, USER_CACHE_EVENT } from 'utils/UserAuthorization';
import { getUserRoleValue, canAccessAudit, getEffectiveMenuItems, hasGeneralAccess } from 'utils/rolePermissions';
import { TemaFixoClaro } from 'theme/tema';
import { hasClientSession } from 'utils/clientSession';
import { getRouteLoader } from './routeLoaders';

const Dashboard = lazy(getRouteLoader('/dashboard'));
const SystemPresentation = lazy(() => import('pages/SystemPresentation'));
const Termos = lazy(() => import('pages/Legal').then((m) => ({ default: m.Termos })));
const Privacidade = lazy(() => import('pages/Legal').then((m) => ({ default: m.Privacidade })));
const ClientPortalLogin = lazy(() => import('pages/ClientPortal/Login'));
const ClientPortalDashboard = lazy(() => import('pages/ClientPortal/Dashboard'));
const ActiveProcesses = lazy(getRouteLoader('/active-processes'));
const ConcludedProcesses = lazy(getRouteLoader('/concluded-processes'));
const UploadData = lazy(getRouteLoader('/upload'));
const Login = lazy(() => import('pages/Login'));
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const Registrations = lazy(getRouteLoader('/registrations'));
const Settings = lazy(getRouteLoader('/settings'));
const Company = lazy(getRouteLoader('/company'));
const NotFound = lazy(() => import('pages/NotFound'));
const Clients = lazy(getRouteLoader('/clients'));
const Schedule = lazy(getRouteLoader('/schedule'));
const Notifications = lazy(getRouteLoader('/notifications'));
const Perfil = lazy(getRouteLoader('/perfil'));
const Desempenho = lazy(getRouteLoader('/desempenho'));
const BusinessIntelligence = lazy(getRouteLoader('/business-intelligence'));
const AdminNotifications = lazy(getRouteLoader('/admin-notifications'));
const KanbanJuridico = lazy(getRouteLoader('/legal-kanban'));
const DocumentTemplates = lazy(getRouteLoader('/document-templates'));
const DocumentEditor = lazy(() => import('pages/DocumentEditor'));
const Financeiro = lazy(getRouteLoader('/financeiro'));
const Publications = lazy(getRouteLoader('/publications'));
const Auditoria = lazy(getRouteLoader('/auditoria'));
const SuperAdminCompanies = lazy(getRouteLoader('/plataforma-admin'));
const Monitors = lazy(getRouteLoader('/monitors'));
const UiKit = lazy(() => import('pages/UiKit'));
const ConsultaEnvolvido = lazy(getRouteLoader('/consulta-envolvido'));
const YrmAcademy = lazy(() => import('pages/YrmAcademy'));

// Portal do cliente, login, cadastro e recuperação de senha: sempre no claro da marca
const ROTAS_PUBLICAS = ['/portal-cliente', '/login', '/cadastro', '/recuperar-senha', '/redefinir-senha', '/apresentacao']
function TemaDaRota() {
  const { pathname } = useLocation()
  return <TemaFixoClaro ativo={pathname === '/' || ROTAS_PUBLICAS.some((r) => pathname.startsWith(r))} />
}

const ClientPrivatePage = ({ children }) => {
  const location = useLocation()
  return hasClientSession()
    ? <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
    : <Navigate to='/portal-cliente/login' replace />
}

// O que importa para decidir as rotas permitidas
const permissionKey = (user) => JSON.stringify([
  user?.token || null,
  user?.userRole || null,
  Boolean(user?.admin),
  Boolean(user?.super_admin),
  user?.effectiveMenuItems || null,
])

const Router = () => {
  const { isLogged } = useLogin()
  // Recalcula as rotas permitidas quando o /me atualiza o cache (Menu) — antes a
  // leitura era única e o guard ficava com permissões antigas até recarregar.
  const [userData, setUserData] = useState(() => getUserCachedData())
  useEffect(() => {
    // Só troca o estado se mudou algo que afeta as rotas. Trocar a cada
    // atualização do cache remontava a página aberta (as rotas usam
    // Page={() => ...}), e telas que atualizam o cache ao abrir — como a
    // Agenda — entravam em loop: piscavam e travavam a navegação.
    const refresh = () => setUserData((prev) => {
      const next = getUserCachedData()
      return permissionKey(prev) === permissionKey(next) ? prev : next
    })
    window.addEventListener(USER_CACHE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(USER_CACHE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])
  useEffect(() => {
    setUserData((prev) => {
      const next = getUserCachedData()
      return permissionKey(prev) === permissionKey(next) ? prev : next
    })
  }, [isLogged])
  const userRole = getUserRoleValue(userData)
  const effectiveMenuItems = getEffectiveMenuItems(userData)
  // Auditoria: cargo E plano precisam liberar (o cargo já checava; agora o
  // plano da empresa também precisa incluir a feature 'auditoria').
  const canSeeAudit = canAccessAudit(userRole) && effectiveMenuItems.includes('auditoria')
  const canSeeFinancial = effectiveMenuItems.includes('financeiro')
  const canSeeDocumentTemplates = effectiveMenuItems.includes('document-templates')
  const canSeeKanban = effectiveMenuItems.includes('legal-kanban')
  const canSeeConsultaEnvolvido = effectiveMenuItems.includes('consulta-envolvido')
  const userHasGeneralAccess = effectiveMenuItems.includes('upload')
  const canSeePlataformaAdmin = effectiveMenuItems.includes('plataforma-admin')
  // página inicial escolhida no perfil, se o usuário ainda tiver acesso a ela
  const paginaInicial = userData?.preferencias?.paginaInicial
  const homePath = paginaInicial && effectiveMenuItems.includes(paginaInicial)
    ? `/${paginaInicial}`
    : canSeePlataformaAdmin ? '/plataforma-admin' : '/dashboard'

  return (
    <BrowserRouter>
      <TemaDaRota />
      <TituloDaAba />
      <Suspense fallback={<Loader />}>
      <Routes>
        {/* Portal do Cliente - público */}
        <Route path='/apresentacao' element={<SystemPresentation />} />
        <Route path='/termos' element={<Termos />} />
        <Route path='/privacidade' element={<Privacidade />} />
        <Route path='/portal-cliente/login' element={<ClientPortalLogin />} />
        <Route
          path='/portal-cliente/dashboard'
          element={(
            <ClientPrivatePage>
              <ClientPortalDashboard />
            </ClientPrivatePage>
          )}
        />

        <Route
          path='/login'
          element={!isLogged ? <Login /> : <Navigate to={homePath} replace />}
        />
        <Route
          path='/recuperar-senha'
          element={!isLogged ? <ForgotPassword /> : <Navigate to={homePath} replace />}
        />
        <Route
          path='/redefinir-senha'
          element={!isLogged ? <ResetPassword /> : <Navigate to={homePath} replace />}
        />
        <Route
          path='/cadastro'
          element={<Navigate to={isLogged ? homePath : '/login'} replace />}
        />

        {/* Rota global para editor sem menu */}
        <Route
          path='/document-editor/:id'
          element={<PrivatePage Page={() => <DocumentEditor />} />}
        />

        <Route
          path='/'
          element={isLogged ? <Container /> : <Navigate to='/login' replace />}
        >
          <Route path='' element={<Navigate to={homePath} replace />} />

          <Route
            path='plataforma-admin'
            element={canSeePlataformaAdmin ? (
              <PrivatePage Page={() => <SuperAdminCompanies />} />
            ) : (
              <Navigate to='/dashboard' replace />
            )}
          />
          <Route path='platform-admin' element={<Navigate to='/plataforma-admin' replace />} />

          <Route path='dashboard' element={<PrivatePage Page={() => <Dashboard />} />} />
          <Route
            path='active-processes'
            element={<PrivatePage Page={() => <ActiveProcesses />} />}
          />
          <Route
            path='/legal-kanban'
            element={canSeeKanban ? (
              <PrivatePage Page={() => <KanbanJuridico />} />
            ) : (
              <Navigate to='/dashboard' replace />
            )}
          />
          <Route
            path='financeiro'
            element={canSeeFinancial ? (
              <PrivatePage Page={() => <Financeiro />} />
            ) : (
              <Navigate to='/dashboard' replace />
            )}
          />
          <Route
            path='monitors'
            element={<PrivatePage Page={() => <Monitors />} />}
          />
          <Route path='clients' element={<PrivatePage Page={() => <Clients />} />} />
          <Route path='schedule' element={<PrivatePage Page={() => <Schedule />} />} />
          <Route
            path='concluded-processes'
            element={<PrivatePage Page={() => <ConcludedProcesses />} />}
          />

          <Route
            path='upload'
            element={
              userHasGeneralAccess ? (
                <PrivatePage Page={() => <UploadData />} />
              ) : (
                <Navigate to='/dashboard' replace />
              )
            }
          />

          <Route path='registrations' element={<PrivatePage Page={() => <Registrations />} />} />
          <Route path='settings' element={<PrivatePage Page={() => <Settings />} />} />
          <Route path='yrm-academy' element={<PrivatePage Page={() => <YrmAcademy />} />} />
          <Route path='company' element={<PrivatePage Page={() => <Company />} />} />
          <Route path='notifications' element={<PrivatePage Page={() => <Notifications />} />} />
          <Route path='perfil' element={<PrivatePage Page={() => <Perfil />} />} />
          <Route
            path='desempenho'
            element={<Desempenho />}
          />
          <Route
            path='business-intelligence'
            element={hasGeneralAccess(userRole) ? <PrivatePage Page={() => <BusinessIntelligence />} /> : <Navigate to={homePath} replace />}
          />
          {process.env.NODE_ENV !== 'production' && (
            <Route path='ui-kit' element={<PrivatePage Page={() => <UiKit />} />} />
          )}

          <Route path='jurimetria' element={<Navigate to='/dashboard' replace />} />
          <Route
            path='consulta-envolvido'
            element={canSeeConsultaEnvolvido ? (
              <PrivatePage Page={() => <ConsultaEnvolvido />} />
            ) : (
              <Navigate to='/dashboard' replace />
            )}
          />
          <Route
            path='publications'
            element={<PrivatePage Page={() => <Publications />} />}
          />
          <Route
            path='admin-notifications'
            element={
              userHasGeneralAccess ? (
                <PrivatePage Page={() => <AdminNotifications />} />
              ) : (
                <Navigate to='/dashboard' replace />
              )
            }
          />
          <Route
            path='auditoria'
            element={
              canSeeAudit ? (
                <PrivatePage Page={() => <Auditoria />} />
              ) : (
                <Navigate to='/dashboard' replace />
              )
            }
          />
        { /* <Route
            path='template'
            element={<PrivatePage Page={() => <Template />} />}
          />*/}
          <Route
            path='document-templates'
            element={canSeeDocumentTemplates ? (
              <PrivatePage Page={() => <DocumentTemplates />} />
            ) : (
              <Navigate to='/dashboard' replace />
            )}
          />

          <Route path='*' element={<PrivatePage Page={() => <NotFound />} />} />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default Router
